/**
 * MOD-01b-4 (Betreiberfreigabe 13.10., D4/Soll 4) – Ops-Center, Mandanten-Detail: Bereich
 * „Abrechnung" (nur lesend) auf Basis von GET /api/ops/tenants/{id}/billing-periods (2a/2b).
 *
 * Je Abrechnungszeitraum (neu → alt) die Positionen mit abrechnungsrelevant / im Voraus
 * berechnet / Nachberechnung (Status, Menge, Grund, Rechnungstext); darunter die Modulbuchungen
 * mit Sofortrechnung. Hervorhebung (rot) bei Nachberechnung „fehlgeschlagen" sowie Sofortrechnung
 * „fehlgeschlagen" oder „offen" älter als 14 Tage (ab booked_at). Keine Schreibaktion, kein Stripe.
 * Daten TT.MM.JJJJ (UTC), exklusive Zeitraum-Enden als letzter Tag (wie Rechnungstexte 2a).
 * ZAHL-02b (Betreiberfreigabe 13.10., V5): zusätzlich erfasste Rücklastschriften/Zahlungsstreite
 * (`rueckbuchungen` aus demselben Endpunkt) rot markiert – reiner Hinweis, keine Sperre.
 * SAMMEL-A3 (Betreiberfreigabe 22.10.): Spaltenkopf „Zugänge Vormonat", Hinweis „Buchungsmonat – …" bei Quelle buchung:,
 * Sofortrechnung „keine – Einzug mit der Monatsrechnung", Endabrechnungs-Zelle mit „Überspringen" (Vertragsende).
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Receipt } from "lucide-react";
import { opsApi, opsErrorMessage } from "@/lib/opsApi";
import { fmtTag } from "@/components/ZusatzmoduleAbschnitt";
// ZAHL-02e (Betreiberfreigabe 14.10., Soll 3): derselbe Knopf wie unter „Offene Vorgänge".
import { ErgebnisText, ErneutVersuchenKnopf, zuErgebnis } from "./OpsOffeneVorgaengeSection";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const OFFEN_WARN_TAGE = 14;

const NACHBERECHNUNG_LABEL = {
  erzeugt: "erzeugt",
  uebersprungen: "übersprungen",
  fehlgeschlagen: "fehlgeschlagen",
  in_arbeit: "in Arbeit",
};

const BUCHUNG_STATUS_LABEL = {
  aktiv: "aktiv",
  abbestellt: "abbestellt",
  beendet: "beendet",
  in_arbeit: "in Arbeit",
  fehlgeschlagen: "fehlgeschlagen",
};

const SOFORT_LABEL = {
  keine: "keine – Einzug mit der Monatsrechnung", // SAMMEL-A3, Betreiberfreigabe 22.10. (Soll 5)
  entwurf: "Entwurf",
  offen: "offen",
  bezahlt: "bezahlt",
  fehlgeschlagen: "fehlgeschlagen",
};

const MODUL_NAME = {
  fuehrerschein: "Führerscheinkontrolle",
  aushang_addon: "Aushangpflichtige Gesetze",
  mehrsprachigkeit: "Unterweisung in Muttersprache",
};

// SAMMEL-A3, Betreiberfreigabe 22.10. (Soll 4): Vorausmenge aus der Buchung (Quelle buchung:<bid>).
const BUCHUNGSMONAT_HINWEIS = "Buchungsmonat – Abzug für vor der Buchung ausgeschiedene Mitarbeiter";

// ZAHL-02b (Betreiberfreigabe 13.10., V5): Rücklastschriften/Zahlungsstreite (nur lesend).
const RUECKBUCHUNG_ART = { abo: "Abo", modul: "Zusatzmodul", unbekannt: "unbekannt" };

const dash = (v) => (v == null || v === "" ? "–" : v);

export function fmtBetrag(cents, currency) {
  if (cents == null || Number.isNaN(Number(cents))) return "–";
  const cur = (currency || "eur").toUpperCase();
  return `${(Number(cents) / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
}

export function zeitraumText(z) {
  const von = fmtTag(z.period_start);
  const bis = fmtTag(z.period_end, { ende: true });
  return von && bis ? `${von}–${bis}` : dash(von || bis);
}

export function sofortrechnungWarnung(b, now = Date.now()) {
  const s = b && b.sofortrechnung ? b.sofortrechnung.status : null;
  if (s === "fehlgeschlagen") return true;
  if (s !== "offen") return false;
  const t = Date.parse(b.booked_at || "");
  return !Number.isNaN(t) && now - t > OFFEN_WARN_TAGE * 24 * 60 * 60 * 1000;
}

function NachberechnungZelle({ n, testId, zeitraum, onRetryDone, ergebnisse = {} }) {
  if (!n) {
    return (
      <TableCell className="text-sm text-slate-400" data-testid={testId}>
        –
      </TableCell>
    );
  }
  const warn = n.status === "fehlgeschlagen";
  // ZAHL-02d (Betreiberfreigabe 14.10., Soll 6): Endabrechnung bei Vertragsende (eigener Modus, Einmalrechnung).
  const endabrechnung = n.mode === "endabrechnung";
  const ergebnis = n.id ? ergebnisse[n.id] : null;
  return (
    <TableCell
      className={`text-sm ${warn ? "bg-red-50 text-red-700 font-semibold" : "text-slate-700"}`}
      data-testid={testId}
      data-warn={warn ? "1" : "0"}
      data-mode={n.mode || ""}
    >
      <div>
        {endabrechnung ? "Endabrechnung · " : ""}
        {NACHBERECHNUNG_LABEL[n.status] || dash(n.status)}
        {n.units != null && <span className="text-slate-500 font-normal"> · Menge {n.units}</span>}
        {endabrechnung && n.payment_status && (
          <span className="text-slate-500 font-normal"> · Zahlung {n.payment_status}</span>
        )}
      </div>
      {n.grund && <div className="text-xs text-slate-500 font-normal">Grund: {n.grund}</div>}
      {n.text && <div className="text-xs text-slate-500 font-normal italic">{n.text}</div>}
      {/* ZAHL-02e (Betreiberfreigabe 14.10., Soll 3/6): Hinweistext + Knopf „Erneut versuchen" nur bei fehlgeschlagen;
          das Ergebnis bleibt auch nach dem Neuladen (Hinweis weg) sichtbar. */}
      {warn && n.hinweis && <div className="text-xs font-normal" data-testid={`${testId}-hinweis`}>{n.hinweis}</div>}
      {ergebnis ? (
        <div className="pt-1 font-normal"><ErgebnisText ergebnis={ergebnis} testId={`${testId}-retry-ergebnis`} /></div>
      ) : warn && n.id && (
        <div className="pt-1 font-normal">
          {/* SAMMEL-A3, Betreiberfreigabe 22.10. (Soll 6): Endabrechnung = Vertragsende → „Überspringen". */}
          <ErneutVersuchenKnopf trueupId={n.id} position={zeitraum.position} zeitraum={zeitraum.text} vertragsende={endabrechnung} onDone={(r) => onRetryDone(n.id, r)} testId={`${testId}-retry`} />
        </div>
      )}
    </TableCell>
  );
}

function ZeitraumBlock({ z, index, onRetryDone, ergebnisse }) {
  const id = `ops-abrechnung-zeitraum-${index}`;
  return (
    <div className="rounded-md border border-slate-200" data-testid={id}>
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-sm font-medium text-slate-800">
        <span data-testid={`${id}-zeitraum`}>{zeitraumText(z)}</span>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid={`${id}-positionen`}>
          <TableHeader>
            <TableRow>
              <TableHead>Position</TableHead>
              <TableHead className="text-right">Abrechnungsrelevant</TableHead>
              <TableHead className="text-right">Im Voraus berechnet</TableHead>
              <TableHead>Zugänge Vormonat</TableHead>{/* SAMMEL-A3, Betreiberfreigabe 22.10. (Soll 3) */}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(z.positionen || []).map((p) => {
              const pid = `${id}-position-${p.position_id}`;
              return (
                <TableRow key={p.position_id} data-testid={pid}>
                  <TableCell className="text-sm text-slate-900">{p.name || p.position_id}</TableCell>
                  <TableCell className="text-right text-sm" data-testid={`${pid}-relevant`}>
                    {dash(p.abrechnungsrelevant)}
                  </TableCell>
                  <TableCell className="text-right text-sm" data-testid={`${pid}-berechnet`}>
                    {dash(p.berechnet)}
                    {/* SAMMEL-A3, Betreiberfreigabe 22.10. (Soll 4) */}
                    {String(p.berechnet_quelle || "").startsWith("buchung:") && (
                      <div className="text-xs text-slate-500" data-testid={`${pid}-berechnet-hinweis`}>{BUCHUNGSMONAT_HINWEIS}</div>
                    )}
                  </TableCell>
                  <NachberechnungZelle
                    n={p.nachberechnung}
                    testId={`${pid}-nachberechnung`}
                    zeitraum={{ position: p.name || p.position_id, text: zeitraumText(z) }}
                    onRetryDone={onRetryDone}
                    ergebnisse={ergebnisse}
                  />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ModulbuchungenTabelle({ buchungen }) {
  return (
    <div className="overflow-x-auto">
      <Table data-testid="ops-abrechnung-modulbuchungen">
        <TableHeader>
          <TableRow>
            <TableHead>Modul</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Gebucht am</TableHead>
            <TableHead>Abbestellt zum</TableHead>
            <TableHead>Sofortrechnung</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {buchungen.map((b) => {
            const bid = `ops-abrechnung-buchung-${b.module_id}`;
            const warn = sofortrechnungWarnung(b);
            const s = b.sofortrechnung || null;
            return (
              <TableRow key={`${b.module_id}-${b.booked_at || ""}`} data-testid={bid} data-warn={warn ? "1" : "0"}>
                <TableCell className="text-sm text-slate-900">{MODUL_NAME[b.module_id] || b.module_id}</TableCell>
                <TableCell className="text-sm" data-testid={`${bid}-status`}>
                  {BUCHUNG_STATUS_LABEL[b.status] || dash(b.status)}
                </TableCell>
                <TableCell className="text-sm" data-testid={`${bid}-gebucht`}>
                  {dash(fmtTag(b.booked_at))}
                </TableCell>
                <TableCell className="text-sm" data-testid={`${bid}-abbestellt`}>
                  {dash(fmtTag(b.cancel_effective_at, { ende: true }))}
                </TableCell>
                <TableCell
                  className={`text-sm ${warn ? "bg-red-50 text-red-700 font-semibold" : ""}`}
                  data-testid={`${bid}-sofortrechnung`}
                >
                  {s ? (
                    <>
                      {SOFORT_LABEL[s.status] || dash(s.status)}
                      {s.units != null && <span className="text-slate-500 font-normal"> · Menge {s.units}</span>}
                    </>
                  ) : (
                    "–"
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ZAHL-02b (V5): Rücklastschriften rot markiert – Datum, Art (Abo/Modul), Rechnung, Betrag, Grund, Status.
function RueckbuchungenTabelle({ rueckbuchungen }) {
  return (
    <div data-testid="ops-abrechnung-rueckbuchungen">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-2">
        Rücklastschriften / Zahlungsstreite
      </h4>
      <p className="text-xs text-red-700 mb-2" data-testid="ops-abrechnung-rueckbuchungen-hinweis">
        Für diesen Mandanten liegen Rücklastschriften vor. Keine automatische Sperre – bitte prüfen.
      </p>
      <div className="overflow-x-auto">
        <Table data-testid="ops-abrechnung-rueckbuchungen-tabelle">
          <TableHeader>
            <TableRow>
              <TableHead>Datum</TableHead>
              <TableHead>Art</TableHead>
              <TableHead>Rechnung</TableHead>
              <TableHead className="text-right">Betrag</TableHead>
              <TableHead>Grund</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rueckbuchungen.map((r) => (
              <TableRow
                key={r.dispute_id}
                className="bg-red-50 text-red-700"
                data-testid={`ops-abrechnung-rueckbuchung-${r.dispute_id}`}
                data-warn="1"
              >
                <TableCell className="text-sm font-semibold">{dash(fmtTag(r.created_at))}</TableCell>
                <TableCell className="text-sm">{RUECKBUCHUNG_ART[r.art] || dash(r.art)}</TableCell>
                <TableCell className="font-mono text-xs">{dash(r.stripe_invoice_id)}</TableCell>
                <TableCell className="text-sm text-right">{fmtBetrag(r.amount_cents, r.currency)}</TableCell>
                <TableCell className="text-sm">{dash(r.reason)}</TableCell>
                <TableCell className="text-sm">{dash(r.dispute_status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function OpsAbrechnungSection({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ZAHL-02e (Soll 2/3): nach einem erfolgreichen „Erneut versuchen" neu laden – der rote Hinweis verschwindet.
  const load = useCallback(() => {
    if (!tenantId) return undefined;
    // Guard analog WeltenPaketSection: hält Tests grün, die opsApi nur partiell mocken.
    if (typeof opsApi.tenantBillingPeriods !== "function") return undefined;
    let active = true;
    setLoading(true);
    setError("");
    opsApi
      .tenantBillingPeriods(tenantId)
      .then((d) => {
        if (active) setData(d);
      })
      .catch((e) => {
        if (active) setError(opsErrorMessage(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tenantId]);

  useEffect(() => load(), [load]);
  // Ergebnisse des Knopfs je Datensatz-ID (bleiben über das Neuladen hinweg sichtbar).
  const [ergebnisse, setErgebnisse] = useState({});
  const onRetryDone = (id, r) => {
    setErgebnisse((prev) => ({ ...prev, [id]: zuErgebnis(r) }));
    if (r.ergebnis !== "fehlgeschlagen") setTimeout(load, 0);
  };

  const zeitraeume = (data?.zeitraeume || [])
    .slice()
    .sort((a, b) => String(b.period_start || "").localeCompare(String(a.period_start || "")));
  const buchungen = data?.modulbuchungen || [];
  const rueckbuchungen = data?.rueckbuchungen || [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="ops-detail-abrechnung">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
        <Receipt className="w-4 h-4 text-slate-500" />
        <h3 className="font-heading text-sm font-semibold text-slate-800">Abrechnung</h3>
        {data && <span className="text-xs text-slate-400">({zeitraeume.length})</span>}
      </div>
      <div className="p-4 space-y-4">
        {loading ? (
          <div className="py-4 flex justify-center" data-testid="ops-abrechnung-loading">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600" data-testid="ops-abrechnung-error">
            {error}
          </p>
        ) : !data ? null : (
          <>
            {rueckbuchungen.length > 0 && <RueckbuchungenTabelle rueckbuchungen={rueckbuchungen} />}
            {zeitraeume.length === 0 ? (
              <p className="text-sm text-slate-400 italic" data-testid="ops-abrechnung-empty">
                Noch keine Abrechnungszeiträume.
              </p>
            ) : (
              <div className="space-y-3" data-testid="ops-abrechnung-zeitraeume">
                {zeitraeume.map((z, i) => (
                  <ZeitraumBlock key={z.period_start || i} z={z} index={i} onRetryDone={onRetryDone} ergebnisse={ergebnisse} />
                ))}
              </div>
            )}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Modulbuchungen</h4>
              {buchungen.length === 0 ? (
                <p className="text-sm text-slate-400 italic" data-testid="ops-abrechnung-buchungen-empty">
                  Keine Modulbuchungen.
                </p>
              ) : (
                <ModulbuchungenTabelle buchungen={buchungen} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
